import React, { useState } from 'react';
import { YouTubeIcon } from '@/components/icons/social';
import AnchorLink from '@/components/ui/links/anchor-link';
import Button from '@/components/ui/button-old';
import Input from '@/components/ui/forms/input';
import routes from '@/config/routes';
import { useIsDarkMode } from '@/lib/hooks/use-is-dark-mode';
import { useMutation } from 'react-query';
import client from '@/data/client';
import toast from 'react-hot-toast';
import { EyeIcon } from '@/components/icons/eye-icon';
import { HeartIcon } from '@/components/icons/heart-icon';
import { ChatIcon } from '@/components/icons/chat-icon';
import { Nav } from '@/lib/constants/nav';
import { trackEvent } from '@/data/utils/utils';
import { useRouter } from 'next/router';
import axios from 'axios';

/* const allSubItems = Nav.flatMap((item) => item.subItems); */

const Footer = () => {
  const { isDarkMode } = useIsDarkMode();
  const [email, setEmail] = useState('');
  const router = useRouter();
  const isRoot = router.pathname === '/';

  const { mutate } = useMutation(
    (data: { email: string }) =>
      axios.post('/api/subscribe-to-newsletter', { ...data, seller: false }),
    {
      onSuccess: () => {
        toast.success('Successfully subscribed to newsletter');
        setEmail('');
      },
      onError: () => {
        toast.error('Oops! Something went wrong');
      },
    }
  );

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    mutate({ email });
    trackEvent('subscribe', {
      method: 'email',
      seller: false,
    });
  };

  return (
    <footer className="hidden bg-light-200 px-4 pt-8 pb-6 dark:bg-dark-100 sm:block sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Top section with icons */}
        {isRoot && (
          <div className="mb-12 flex flex-wrap justify-between border-b border-gray-300 pb-6 dark:border-dark-400">
            <div className="mb-4 flex items-center md:mb-0">
              <EyeIcon className="mr-3 h-8 w-8" />
              <div>
                <h4 className="font-bold">Quality assets</h4>
                <p className="text-sm text-gray-600 dark:text-dark-700">
                  Reviewed by professionals
                </p>
              </div>
            </div>
            <div className="mb-4 flex items-center md:mb-0">
              <HeartIcon className="mr-3 h-8 w-8" />
              <div>
                <h4 className="font-bold">Trusted</h4>
                <p className="text-sm text-gray-600 dark:text-dark-700">
                  Rated by customers
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <ChatIcon className="mr-3 h-8 w-8" />
              <div>
                <h4 className="font-bold">Community support</h4>
                <p className="text-sm text-gray-600 dark:text-dark-700">
                  Supported by community members
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main footer content */}
        <div className="mb-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <div className="col-span-1 sm:col-span-2 xl:col-span-1">
            <h3 className="mb-4 text-lg font-bold">Resources</h3>
            <ul className="space-y-2">
              <li>
                <AnchorLink href={routes.blog}>Blog</AnchorLink>
              </li>
              <li>
                <AnchorLink href={routes.home}>
                  Explore AI Prompts & Models
                </AnchorLink>
              </li>
              {/* <li><AnchorLink href={routes.about}>About</AnchorLink></li> */}
              {/* <li><AnchorLink href={routes.contact}>Contact</AnchorLink></li> */}
            </ul>
          </div>
          <div>
            <h3 className="mb-4 text-lg font-bold">Legal</h3>
            <ul className="space-y-2">
              <li>
                <AnchorLink href={routes.terms}>Terms of Service</AnchorLink>
              </li>
              <li>
                <AnchorLink href={routes.privacy}>Privacy Policy</AnchorLink>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="mb-4 text-lg font-bold">Support</h3>
            <ul className="space-y-2">
              <li>
                <AnchorLink href={routes.help}>FAQ</AnchorLink>
              </li>
              <li>
                <AnchorLink href={routes.contact}>Customer Service</AnchorLink>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="mb-4 text-lg font-bold">Categories</h3>
            <ul className="space-y-2">
              <li>
                <AnchorLink href="/?category=image&orderBy=popularity">
                  Best AI Image Prompts
                </AnchorLink>
              </li>
              <li>
                <AnchorLink href="/?category=text&orderBy=popularity">
                  Best AI Text Prompts
                </AnchorLink>
              </li>
              {Nav.map((item) => (
                <li key={item.label}>
                  <AnchorLink href={item.href}>
                    AI Prompts for {item.label}
                  </AnchorLink>
                </li>
              ))}
              {/*  {allSubItems.map((item) => (
                <li key={item.label}><AnchorLink href={item.href}>{item.label}</AnchorLink></li>
              ))} */}
            </ul>
          </div>
          <div>
            <h3 className="mb-4 text-lg font-bold">Models</h3>
            <ul className="space-y-2">
              <li>
                <AnchorLink href="/?aiModels=gpt">ChatGPT</AnchorLink>
              </li>
              <li>
                <AnchorLink href="/?aiModels=midjourney">Midjourney</AnchorLink>
              </li>
            </ul>
          </div>
          <div className="col-span-1 sm:col-span-2 xl:col-span-2">
            <div className="mb-6">
              <h3 className="mb-4 text-lg font-bold">Follow Us</h3>
              <div className="flex space-x-4">
                <a
                  href="https://www.youtube.com/@PromptsClub"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-opacity hover:opacity-80"
                >
                  <YouTubeIcon
                    className="h-6 w-6"
                    color={isDarkMode ? 'white' : 'black'}
                    aria-label="YouTube Prompts Club"
                  />
                </a>
                <a
                  href="https://twitter.com/PromptsClub"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-opacity hover:opacity-80"
                >
                  <div className="h-6 w-6">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 300 271"
                      fill={isDarkMode ? 'white' : 'black'}
                      aria-label="Twitter Prompts Club"
                    >
                      <path d="M236 0h46L181 115l118 156h-92.6l-72.5-94.8-83 94.8h-46l107-123L-1.1 0h94.9l65.5 86.6zm-16.1 244h25.5L80.4 26H53z"></path>
                    </svg>
                  </div>
                </a>
              </div>
            </div>
            <div className="w-full max-w-lg">
              <h3 className="mb-4 text-lg font-bold">Newsletter</h3>
              <form onSubmit={handleSubscribe} className="space-y-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full"
                  // label="Email" // Add this line to fix the TypeScript error
                />
                <Button type="submit" className="w-full">
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-gray-300 pt-6 text-center dark:border-dark-400 sm:text-left">
          <p className="text-sm text-gray-600 dark:text-dark-600">
            &copy; {new Date().getFullYear()} All rights reserved by Prompts
            Club
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
